import type {
  MatchedPair,
  NotInRnicDecision,
  RnicNotInFileDecision,
} from './ImportDetail.module';

export type ForcedTypeLabel = 'Copropriété' | 'ASL' | 'Tertiaire' | 'AFUL' | 'Autre';

export interface ManualTreatmentPdfEntry {
  key: string;
  name: string;
  immatriculation: string;
  address?: string;
  matchedWith?: {
    name: string;
    immatriculation: string;
    address?: string;
  };
  typeLabel?: ForcedTypeLabel;
  note?: string;
  source: 'file' | 'rnic' | 'match';
}

export interface ManualTreatmentPdfClassification {
  adjustments: ManualTreatmentPdfEntry[];
  aslAfulTertiaire: ManualTreatmentPdfEntry[];
  ignoredFile: ManualTreatmentPdfEntry[];
  ignoredRnic: ManualTreatmentPdfEntry[];
  forcedRnic: ManualTreatmentPdfEntry[];
  success: ManualTreatmentPdfEntry[];
  errors: string[];
}

export const forcedTypeLabels: Record<number, ForcedTypeLabel> = {
  1: 'Copropriété',
  2: 'ASL',
  3: 'Tertiaire',
  4: 'AFUL',
  5: 'Autre',
};

const generateEntryKey = (immatriculation: string, name: string): string => {
  const immatPart = immatriculation?.trim() || '';
  const namePart = name?.trim() || '';
  return `${immatPart}::${namePart}`;
};

interface ImportSuccessCopro {
  immatriculation: string;
  name: string;
  rnicName?: string;
  rnicAddress?: string;
}

interface ClassificationParams {
  matchedPairs: MatchedPair[];
  notInRnicDecisions: NotInRnicDecision[];
  rnicNotInFileDecisions: RnicNotInFileDecision[];
  fileAddressMap?: Map<string, string>;
  rnicAddressMap?: Map<string, string>;
  importSuccessCopros?: ImportSuccessCopro[];
}

const recordAssignment = (
  registry: Map<string, string>,
  key: string,
  category: string,
  errors: string[],
) => {
  if (!key) {
    return;
  }
  const existing = registry.get(key);
  if (existing) {
    errors.push(`L'entrée ${key} est déjà classée dans "${existing}" et ne peut pas aussi être dans "${category}".`);
    return;
  }
  registry.set(key, category);
};

export function classifyManualTreatmentForPdf({
  matchedPairs,
  notInRnicDecisions,
  rnicNotInFileDecisions,
  fileAddressMap,
  rnicAddressMap,
  importSuccessCopros,
}: ClassificationParams): ManualTreatmentPdfClassification {
  const normalizeImmat = (value?: string): string => (value || '').replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();

  const isManualAdjustmentPair = (pair: MatchedPair): { isManual: boolean; reasons: string[] } => {
    const reasons: string[] = [];
    const immat = pair.fileEntry.immatriculation?.trim() || '';
    const immatMissing = immat === '' || immat === '-';
    const fileImmatNormalized = normalizeImmat(pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation);
    const rnicImmatNormalized = normalizeImmat(pair.rnicEntry.immatriculation);
    const immatMismatch =
      fileImmatNormalized !== '' &&
      rnicImmatNormalized !== '' &&
      fileImmatNormalized !== rnicImmatNormalized;

    if (pair.matchSource === 'address') {
      reasons.push('Correspondance par adresse');
    }
    if (pair.matchSource === 'name') {
      reasons.push('Correspondance par nom');
    }
    if (pair.isManuallyLinked || pair.matchSource === 'manual') {
      reasons.push('Lien créé ou confirmé manuellement');
    }
    if (pair.fileEntry.originalImmatriculation) {
      reasons.push(`Immatriculation corrigée depuis ${pair.fileEntry.originalImmatriculation}`);
    }
    if (immatMissing) {
      reasons.push('Immatriculation absente dans le fichier');
    }
    if (immatMismatch) {
      reasons.push('Immatriculation différente du RNIC');
    }

    return { isManual: reasons.length > 0, reasons };
  };

  const validatedPairs = matchedPairs.filter((pair) => pair.status === 'validated');
  const handledFileKeys = new Set(
    validatedPairs.map((pair) =>
      generateEntryKey(pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation, pair.fileEntry.name),
    ),
  );
  const handledRnicKeys = new Set(
    validatedPairs.map((pair) => generateEntryKey(pair.rnicEntry.immatriculation, pair.rnicEntry.name)),
  );
  const decisionFileKeys = new Set(
    notInRnicDecisions.map((d) => generateEntryKey(d.immatriculation, d.name)),
  );
  const decisionRnicKeys = new Set(
    rnicNotInFileDecisions.map((d) => generateEntryKey(d.immatriculation, d.name)),
  );

  const adjustments: ManualTreatmentPdfEntry[] = [];
  const aslAfulTertiaire: ManualTreatmentPdfEntry[] = [];
  const ignoredFile: ManualTreatmentPdfEntry[] = [];
  const ignoredRnic: ManualTreatmentPdfEntry[] = [];
  const forcedRnic: ManualTreatmentPdfEntry[] = [];
  const success: ManualTreatmentPdfEntry[] = [];
  const errors: string[] = [];

  const fileAssignments = new Map<string, string>();
  const rnicAssignments = new Map<string, string>();

  // Pre-fill "Imports sans ajustement" with entries coming directly from the import data.
  // These entries were never part of the manual treatment workflow.
  importSuccessCopros?.forEach((copro) => {
    const fileKey = generateEntryKey(copro.immatriculation, copro.name);
    const rnicKey = generateEntryKey(copro.immatriculation, copro.rnicName || copro.name);
    const entry: ManualTreatmentPdfEntry = {
      key: fileKey,
      name: copro.name,
      immatriculation: copro.immatriculation,
      address: fileAddressMap?.get(fileKey),
      matchedWith: copro.rnicName
        ? {
            name: copro.rnicName,
            immatriculation: copro.immatriculation,
            address: copro.rnicAddress || rnicAddressMap?.get(rnicKey),
          }
        : undefined,
      source: 'match',
    };

    success.push(entry);
    recordAssignment(fileAssignments, fileKey, 'import-ok', errors);
    recordAssignment(rnicAssignments, rnicKey, 'import-ok', errors);
  });

  matchedPairs.forEach((pair) => {
    const fileKey = generateEntryKey(pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation, pair.fileEntry.name);
    const rnicKey = generateEntryKey(pair.rnicEntry.immatriculation, pair.rnicEntry.name);
    const entry: ManualTreatmentPdfEntry = {
      key: fileKey,
      name: pair.fileEntry.name,
      immatriculation: pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation,
      address: fileAddressMap?.get(fileKey),
      matchedWith: {
        name: pair.rnicEntry.name,
        immatriculation: pair.rnicEntry.immatriculation,
        address: rnicAddressMap?.get(rnicKey),
      },
      note: undefined,
      source: 'match',
    };

    const manualStatus = isManualAdjustmentPair(pair);
    if (manualStatus.reasons.length > 0) {
      entry.note = ['Ajustement manuel', ...manualStatus.reasons].join(' · ');
    }

    const isManualMatch = manualStatus.isManual;
    if (pair.status === 'validated') {
      if (isManualMatch) {
        entry.note ||= 'Ajustement manuel';
        adjustments.push(entry);
        recordAssignment(fileAssignments, fileKey, 'ajustements', errors);
        recordAssignment(rnicAssignments, rnicKey, 'ajustements', errors);
      } else if (!importSuccessCopros || importSuccessCopros.length === 0) {
        // Only rely on manual-treatment data for "imports sans ajustement" when no import data is available.
        success.push(entry);
        recordAssignment(fileAssignments, fileKey, 'import-ok', errors);
        recordAssignment(rnicAssignments, rnicKey, 'import-ok', errors);
      }
    } else {
      const fileAlreadyHandled = handledFileKeys.has(fileKey) || decisionFileKeys.has(fileKey);
      const rnicAlreadyHandled = handledRnicKeys.has(rnicKey) || decisionRnicKeys.has(rnicKey);
      if (!fileAlreadyHandled && !rnicAlreadyHandled) {
        errors.push(`La paire ${fileKey} ↔ ${rnicKey} n'est pas validée (statut: ${pair.status}).`);
      }
    }
  });

  notInRnicDecisions.forEach((decision) => {
    const key = generateEntryKey(decision.immatriculation, decision.name);
    if (handledFileKeys.has(key)) {
      return;
    }
    const address = fileAddressMap?.get(key);
    const typeLabel = decision.forcedType ? forcedTypeLabels[decision.forcedType] : undefined;
    const baseEntry: ManualTreatmentPdfEntry = {
      key,
      name: decision.name,
      immatriculation: decision.immatriculation,
      address,
      typeLabel,
      note: decision.reason || undefined,
      source: 'file',
    };

    if (decision.decision === 'skip') {
      ignoredFile.push(baseEntry);
      recordAssignment(fileAssignments, key, 'ignored-file', errors);
      return;
    }

    if (decision.decision === 'force') {
      if (decision.forcedType && decision.forcedType !== 1) {
        aslAfulTertiaire.push(baseEntry);
        recordAssignment(fileAssignments, key, 'asl-aful-tertiaire', errors);
      } else {
        let note = decision.correctedImmatriculation
          ? `Immatriculation corrigée en ${decision.correctedImmatriculation}`
          : 'Forcé comme copropriété';
        if (decision.reason) {
          note = note ? `${note} — Raison : ${decision.reason}` : `Raison : ${decision.reason}`;
        }
        adjustments.push({
          ...baseEntry,
          note,
        });
        recordAssignment(fileAssignments, key, 'ajustements', errors);
      }
      return;
    }

    if (decision.correctedImmatriculation) {
      let note = `Immatriculation corrigée en ${decision.correctedImmatriculation}`;
      if (decision.reason) {
        note = `${note} — Raison : ${decision.reason}`;
      }
      adjustments.push({
        ...baseEntry,
        note,
      });
      recordAssignment(fileAssignments, key, 'ajustements', errors);
      return;
    }

    errors.push(`L'entrée "${decision.name}" (${decision.immatriculation || 'immat. manquante'}) n'est pas classée.`);
  });

  rnicNotInFileDecisions.forEach((decision) => {
    const key = generateEntryKey(decision.immatriculation, decision.name);
    if (handledRnicKeys.has(key)) {
      return;
    }
    const address = rnicAddressMap?.get(key);
    const baseEntry: ManualTreatmentPdfEntry = {
      key,
      name: decision.name,
      immatriculation: decision.immatriculation,
      address,
      source: 'rnic',
    };

    if (decision.decision === 'ignore') {
      ignoredRnic.push(baseEntry);
      recordAssignment(rnicAssignments, key, 'rnic-ignore', errors);
      return;
    }

    if (decision.decision === 'integrate') {
      forcedRnic.push(baseEntry);
      recordAssignment(rnicAssignments, key, 'rnic-force', errors);
      return;
    }

    errors.push(`L'entrée RNIC "${decision.name}" (${decision.immatriculation || 'immat. manquante'}) n'est pas classée.`);
  });

  notInRnicDecisions.forEach((decision) => {
    const key = generateEntryKey(decision.immatriculation, decision.name);
    if (handledFileKeys.has(key)) {
      return;
    }
    if (!fileAssignments.has(key)) {
      errors.push(`L'entrée du fichier "${decision.name}" (${decision.immatriculation || 'immat. manquante'}) est absente des catégories.`);
    }
  });

  rnicNotInFileDecisions.forEach((decision) => {
    const key = generateEntryKey(decision.immatriculation, decision.name);
    if (handledRnicKeys.has(key)) {
      return;
    }
    if (!rnicAssignments.has(key)) {
      errors.push(`L'entrée RNIC "${decision.name}" (${decision.immatriculation || 'immat. manquante'}) est absente des catégories.`);
    }
  });

  return {
    adjustments,
    aslAfulTertiaire,
    ignoredFile,
    ignoredRnic,
    forcedRnic,
    success,
    errors,
  };
}
